import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const uid = () => Math.random().toString(36).slice(2, 10);
export const kebab = (s) => s.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase());
const SKIP = new Set(["SCRIPT", "STYLE", "LINK", "META", "TITLE", "HEAD", "HTML", "BR"]);
export const TEXT_TAGS = new Set(["H1", "H2", "H3", "H4", "H5", "H6", "P", "SPAN", "A", "BUTTON", "LI", "LABEL", "STRONG", "EM", "B", "I", "SMALL", "BLOCKQUOTE", "TD", "TH", "DIV"]);

const BASE_CSS = "[data-lp]{cursor:default !important} [contenteditable=true]{outline:none !important;cursor:text !important} html{scroll-behavior:auto}";

export function useEditor(project) {
  const iframeRef = useRef(null);
  const [html, setHtml] = useState(project.html);
  const [srcDoc, setSrcDoc] = useState(project.html);
  const [loadKey, setLoadKey] = useState(0);
  const hist = useRef({ stack: [project.html], idx: 0 });
  const [histState, setHistState] = useState({ canUndo: false, canRedo: false });
  const [device, setDevice] = useState("desktop");
  const [selected, setSelected] = useState(null); // data-lp id
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(null);
  const [saved, setSaved] = useState("salvo");
  const [tick, setTick] = useState(0);
  const mobile = useRef({});
  const saveTimer = useRef();
  const typeTimer = useRef();
  const ready = useRef(false);

  const doc = () => iframeRef.current?.contentDocument;
  const el = useCallback((id) => (id ? doc()?.querySelector(`[data-lp="${id}"]`) : null), []);

  const ensureIds = useCallback((root) => {
    root?.querySelectorAll("*").forEach((n) => { if (!SKIP.has(n.tagName) && !n.hasAttribute("data-lp")) n.setAttribute("data-lp", uid()); });
  }, []);

  const writeMobile = useCallback(() => {
    const d = doc(); if (!d) return;
    let s = d.getElementById("lp-mobile");
    if (!s) { s = d.createElement("style"); s.id = "lp-mobile"; d.head.appendChild(s); }
    const rules = Object.entries(mobile.current).map(([id, props]) => { const body = Object.entries(props).filter(([, v]) => v).map(([k, v]) => `${kebab(k)}:${v} !important`).join(";"); return body ? `[data-lp="${id}"]{${body}}` : ""; }).filter(Boolean).join("\n");
    s.textContent = `@media (max-width:767px){\n${rules}\n}`;
  }, []);

  const serialize = useCallback(() => {
    const d = doc(); if (!d) return html;
    const clone = d.documentElement.cloneNode(true);
    clone.querySelectorAll("#lp-editor-style,[contenteditable]").forEach((n) => (n.id === "lp-editor-style" ? n.remove() : n.removeAttribute("contenteditable")));
    return "<!DOCTYPE html>\n" + clone.outerHTML;
  }, [html]);

  const autosave = useCallback((h) => {
    setSaved("salvando");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await api.patch(`/projects/${project.id}`, { html: h }); setSaved("salvo"); }
      catch { setSaved("erro"); setTimeout(() => autosave(h), 4000); }
    }, 1200);
  }, [project.id]);

  const push = useCallback((h) => {
    const s = hist.current; s.stack = s.stack.slice(0, s.idx + 1); s.stack.push(h); if (s.stack.length > 80) s.stack.shift(); s.idx = s.stack.length - 1;
    setHistState({ canUndo: s.idx > 0, canRedo: false });
  }, []);

  const commit = useCallback(() => {
    clearTimeout(typeTimer.current);
    const h = serialize(); setHtml(h); push(h); autosave(h); setTick((t) => t + 1);
  }, [serialize, push, autosave]);

  // typing / execCommand: keep the indicator honest and save without waiting for a blur
  const commitSoon = useCallback(() => {
    setSaved("salvando");
    clearTimeout(typeTimer.current);
    typeTimer.current = setTimeout(() => commitRef.current(), 700);
  }, []);

  const replaceHtml = useCallback((h, { record = true } = {}) => {
    setHtml(h); setSrcDoc(h); if (record) push(h); autosave(h); setSelected(null); setEditing(false); setLoadKey((k) => k + 1);
  }, [push, autosave]);

  const undo = useCallback(() => { const s = hist.current; if (s.idx <= 0) return; s.idx--; setHistState({ canUndo: s.idx > 0, canRedo: true }); replaceHtml(s.stack[s.idx], { record: false }); }, [replaceHtml]);
  const redo = useCallback(() => { const s = hist.current; if (s.idx >= s.stack.length - 1) return; s.idx++; setHistState({ canUndo: true, canRedo: s.idx < s.stack.length - 1 }); replaceHtml(s.stack[s.idx], { record: false }); }, [replaceHtml]);

  // style get/set (device aware)
  const getStyle = useCallback((id, prop) => {
    const e = el(id); if (!e) return "";
    if (device === "mobile") return mobile.current[id]?.[prop] || "";
    return e.style[prop] || "";
  }, [el, device]);
  const getComputed = useCallback((id, prop) => { const e = el(id); return e ? doc().defaultView.getComputedStyle(e)[prop] : ""; }, [el]);
  const setStyle = useCallback((id, prop, val, { commitNow = true } = {}) => {
    const e = el(id); if (!e) return;
    if (device === "mobile") { mobile.current[id] = { ...(mobile.current[id] || {}), [prop]: val }; writeMobile(); }
    else e.style[prop] = val;
    if (commitNow) commit(); else setTick((t) => t + 1);
  }, [el, device, writeMobile, commit]);

  // iframe load
  const onLoad = useCallback(() => {
    const d = doc(); if (!d?.body) return;
    ensureIds(d.body);
    let st = d.getElementById("lp-editor-style");
    if (!st) { st = d.createElement("style"); st.id = "lp-editor-style"; d.head.appendChild(st); }
    st.textContent = BASE_CSS;
    mobile.current = {};
    const ms = d.getElementById("lp-mobile");
    if (ms) for (const m of ms.textContent.matchAll(/\[data-lp="([^"]+)"\]\s*\{([^}]*)\}/g)) { const props = {}; m[2].split(";").forEach((r) => { const [k, ...v] = r.split(":"); if (k?.trim()) props[k.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = v.join(":").replace("!important", "").trim(); }); mobile.current[m[1]] = props; }
    const isTextEl = (t) => TEXT_TAGS.has(t.tagName) && !t.querySelector("img,video,iframe,section,div,ul,form");
    const onClick = (e) => { const t = e.target.closest("[data-lp]"); if (!t) return; const id = t.getAttribute("data-lp"); if (skipClick.current) { skipClick.current = false; e.preventDefault(); e.stopPropagation(); return; } if (t.isContentEditable && id === selectedRef.current) return; e.preventDefault(); e.stopPropagation(); if (id === selectedRef.current && isTextEl(t)) { startEdit(t); return; } endEdit(); setSelected(id); };
    const onDown = (e) => { if (e.button !== 0 || editingRef.current) return; const t = e.target.closest("[data-lp]"); if (!t || t.getAttribute("data-lp") !== selectedRef.current) return; dragStart.current?.(e); };
    const onDbl = (e) => { const t = e.target.closest("[data-lp]"); if (t && isTextEl(t)) { e.preventDefault(); setSelected(t.getAttribute("data-lp")); startEdit(t); } };
    const onOver = (e) => { const t = e.target.closest("[data-lp]"); setHover(t ? t.getAttribute("data-lp") : null); };
    const onScroll = () => setTick((t) => t + 1);
    const onInput = () => commitSoonRef.current();
    const onFocusOut = () => { if (editingEl.current) { clearTimeout(typeTimer.current); commitRef.current(); } };
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redoRef.current() : undoRef.current(); } if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redoRef.current(); } if (e.key === "Escape") { endEdit(); setSelected(null); } };
    d.addEventListener("mousedown", onDown, true);
    d.addEventListener("click", onClick, true); d.addEventListener("dblclick", onDbl, true); d.addEventListener("mouseover", onOver); d.addEventListener("scroll", onScroll, true); d.addEventListener("keydown", onKey);
    d.addEventListener("input", onInput); d.addEventListener("focusout", onFocusOut, true);
    d.defaultView.addEventListener("resize", onScroll);
    d.querySelectorAll("a").forEach((a) => a.addEventListener("click", (e) => e.preventDefault()));
    ready.current = true; setTick((t) => t + 1);
  }, [ensureIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedRef = useRef(null); selectedRef.current = selected;
  const editingRef = useRef(false); editingRef.current = editing;
  const skipClick = useRef(false);
  const dragStart = useRef(null);
  const undoRef = useRef(undo); undoRef.current = undo;
  const redoRef = useRef(redo); redoRef.current = redo;
  const editingEl = useRef(null);
  const startEdit = (t) => {
    const target = forDeviceRef.current(t) || t;
    if (editingEl.current && editingEl.current !== target) endEdit();
    target.setAttribute("contenteditable", "true");
    target.focus();
    const d = doc(), s = d?.getSelection();
    if (s) { const r = d.createRange(); r.selectNodeContents(target); r.collapse(false); s.removeAllRanges(); s.addRange(r); }
    editingEl.current = target; setEditing(true);
  };
  const endEdit = () => { const t = editingEl.current; if (!t) return; t.removeAttribute("contenteditable"); editingEl.current = null; setEditing(false); commitRef.current(); };
  const commitRef = useRef(commit); commitRef.current = commit;
  const endEditRef = useRef(endEdit); endEditRef.current = endEdit;
  const commitSoonRef = useRef(commitSoon); commitSoonRef.current = commitSoon;

  // ---- per-device content variants ----
  const ensureDeviceCss = useCallback(() => {
    const d = doc(); if (!d) return;
    let s = d.getElementById("lp-device");
    if (!s) { s = d.createElement("style"); s.id = "lp-device"; d.head.appendChild(s); }
    s.textContent = '@media (max-width:767px){[data-lp-only="desktop"]{display:none !important}}@media (min-width:768px){[data-lp-only="mobile"]{display:none !important}}';
  }, []);

  /** Returns the element that belongs to the device being edited, splitting it in two the
   * first time the user changes content on mobile so desktop and mobile stop sharing it. */
  const forDevice = useCallback((elx) => {
    if (!elx) return elx;
    const only = elx.getAttribute("data-lp-only");
    const pair = elx.getAttribute("data-lp-pair");
    if (only === device) return elx;
    if (only && pair) return doc()?.querySelector(`[data-lp-pair="${pair}"][data-lp-only="${device}"]`) || elx;
    if (device !== "mobile") return elx;
    ensureDeviceCss();
    const pid = uid();
    const clone = elx.cloneNode(true);
    [clone, ...clone.querySelectorAll("[data-lp]")].forEach((n) => n.removeAttribute("data-lp"));
    elx.setAttribute("data-lp-only", "desktop"); elx.setAttribute("data-lp-pair", pid);
    clone.setAttribute("data-lp-only", "mobile"); clone.setAttribute("data-lp-pair", pid);
    elx.after(clone);
    ensureIds(elx.parentElement);
    const oldId = elx.getAttribute("data-lp"), newId = clone.getAttribute("data-lp");
    if (mobile.current[oldId]) { mobile.current[newId] = { ...mobile.current[oldId] }; writeMobile(); }
    setSelected(newId);
    return clone;
  }, [device, ensureDeviceCss, ensureIds, writeMobile]);
  const forDeviceRef = useRef(forDevice); forDeviceRef.current = forDevice;

  useEffect(() => {
    const st = doc()?.getElementById("lp-editor-style"); if (!st) return;
    st.textContent = BASE_CSS + (selected && !editing ? `[data-lp="${selected}"]{cursor:grab !important}` : "");
  }, [selected, editing, loadKey, tick]);

  useEffect(() => { if (editingEl.current) endEditRef.current(); }, [device]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);
  useEffect(() => { const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); } if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); } }; window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, [undo, redo]);

  // element ops
  const ops = {
    parent: () => { const e = el(selected); const p = e?.parentElement; if (p && p.tagName !== "BODY" && p.hasAttribute("data-lp")) setSelected(p.getAttribute("data-lp")); },
    up: () => { const e = el(selected); if (e?.previousElementSibling) { e.parentElement.insertBefore(e, e.previousElementSibling); commit(); } },
    down: () => { const e = el(selected); if (e?.nextElementSibling) { e.parentElement.insertBefore(e.nextElementSibling, e); commit(); } },
    duplicate: () => { const e = el(selected); if (!e) return; const c = e.cloneNode(true); c.removeAttribute("data-lp"); c.querySelectorAll("[data-lp]").forEach((n) => n.removeAttribute("data-lp")); e.after(c); ensureIds(e.parentElement); setSelected(c.getAttribute("data-lp")); commit(); },
    remove: () => { const e = el(selected); if (!e) return; const p = e.parentElement; e.remove(); setSelected(p?.getAttribute("data-lp") || null); commit(); },
    edit: () => { const e = el(selected); if (e) startEdit(e); },
    insert: (snippet) => { const d = doc(); const wrap = d.createElement("div"); wrap.innerHTML = snippet; const node = wrap.firstElementChild; if (device === "mobile") { ensureDeviceCss(); node.setAttribute("data-lp-only", "mobile"); } const target = el(selected); if (target && target.parentElement && target.tagName !== "BODY") target.after(node); else d.body.appendChild(node); ensureIds(d.body); setSelected(node.getAttribute("data-lp")); node.scrollIntoView({ block: "center" }); commit(); },
    setAttr: (attr, val) => { const e = forDevice(el(selected)); if (!e) return; val ? e.setAttribute(attr, val) : e.removeAttribute(attr); commit(); },
    setText: (val) => { const e = forDevice(el(selected)); if (!e) return; e.textContent = val; commit(); },
    exec: (cmd, val) => { const d = doc(); d.execCommand(cmd, false, val); setTick((t) => t + 1); commitSoon(); },
  };

  return { iframeRef, html, srcDoc, skipClick, dragStart, loadKey, onLoad, device, setDevice, selected, setSelected, hover, editing, endEdit, saved, tick, el, doc, getStyle, getComputed, setStyle, commit, replaceHtml, undo, redo, ...histState, ops, ready, serialize, ensureIds };
}
